import { useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "@/features/api/apiClient";
import {
  deactivateAdminPromoCode,
  getAdminPromoCodeDetail,
  reactivateAdminPromoCode,
  type AdminPromoCodeDetailResponse,
  type AdminPromoCodeSummary,
  type AdminPromoCodeUseSummary,
} from "@/features/admin/adminApi";

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function statusBadgeClass(status: AdminPromoCodeSummary["statusLabel"]): string {
  switch (status) {
    case "Active":
      return "bg-emerald-100 text-emerald-800";
    case "Inactive":
      return "bg-slate-100 text-slate-600";
    case "Expired":
      return "bg-amber-100 text-amber-800";
    case "Exhausted":
      return "bg-orange-100 text-orange-800";
  }
}

function tierLabel(tier: string): string {
  if (tier === "lifetime_standard") return "Lifetime Standard";
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <div className="mt-1 break-all text-sm font-semibold text-slate-800">
        {value ?? "—"}
      </div>
    </div>
  );
}

function CodeSummaryCard({ code }: { code: AdminPromoCodeSummary }) {
  const usageText =
    code.maxUses !== null
      ? `${code.usesCount} of ${code.maxUses} uses`
      : `${code.usesCount} uses`;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-indigo-700">Access Code</p>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(code.statusLabel)}`}
        >
          {code.statusLabel}
        </span>
        <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800">
          {tierLabel(code.tierGranted)}
        </span>
      </div>

      <p className="mt-3 font-mono text-2xl font-bold tracking-wide text-slate-950">
        {code.code}
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <DetailRow label="Tier granted" value={tierLabel(code.tierGranted)} />
        <DetailRow
          label="Duration"
          value={
            code.tierGranted === "lifetime_standard"
              ? "Lifetime"
              : code.accessDurationDays
                ? `${code.accessDurationDays} days`
                : "Indefinite"
          }
        />
        <DetailRow label="Usage" value={usageText} />
        {code.remainingUses !== null ? (
          <DetailRow label="Remaining uses" value={code.remainingUses} />
        ) : null}
        <DetailRow
          label="Expires"
          value={code.expiresAt ? formatDate(code.expiresAt) : "Never"}
        />
        <DetailRow label="Created" value={formatDate(code.createdAt)} />
      </div>
    </section>
  );
}

function UsageHistoryCard({ uses }: { uses: AdminPromoCodeUseSummary[] }) {
  if (uses.length === 0) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-indigo-700">Usage History</p>
        <p className="mt-3 text-sm text-slate-500">
          No redemptions recorded yet.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-indigo-700">
        Usage History ({uses.length} redemption{uses.length !== 1 ? "s" : ""})
      </p>
      <div className="mt-4 grid gap-3">
        {uses.map((use) => (
          <div
            key={use.id}
            className="rounded-md border border-slate-100 bg-slate-50 p-3 text-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800">
                  {use.userName ?? "Unknown user"}
                </p>
                {use.userEmail ? (
                  <p className="text-slate-500">{use.userEmail}</p>
                ) : null}
              </div>
              <div className="text-right text-xs text-slate-400">
                {formatDate(use.usedAt)}
              </div>
            </div>
            {use.userId ? (
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="rounded bg-slate-200 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                  {use.userTier ?? "unknown tier"}
                </span>
                <Link
                  to={`/admin/users/${encodeURIComponent(use.userId)}`}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  View user →
                </Link>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function StatusControlCard({
  code,
  onStatusChanged,
}: {
  code: AdminPromoCodeSummary;
  onStatusChanged: () => void;
}) {
  const { getToken } = useAuth();
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const isDeactivating = code.active;
  const actionLabel = isDeactivating ? "Deactivate" : "Reactivate";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reason.trim() || reason.trim().length < 5) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await getToken();
      if (isDeactivating) {
        await deactivateAdminPromoCode(token, code.id, { reason: reason.trim() });
      } else {
        await reactivateAdminPromoCode(token, code.id, { reason: reason.trim() });
      }
      setShowForm(false);
      setReason("");
      onStatusChanged();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : `${actionLabel} failed.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-indigo-700">Code Status</p>

      <p className="mt-2 text-sm text-slate-600">
        {code.active
          ? "This access code is currently active. Deactivating it will prevent new redemptions."
          : "This access code is inactive. Reactivating it will allow new redemptions if it has not expired or been exhausted."}
      </p>

      {!showForm ? (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className={`mt-3 rounded-md px-4 py-2 text-sm font-semibold text-white transition ${
            isDeactivating
              ? "bg-amber-600 hover:bg-amber-700"
              : "bg-indigo-600 hover:bg-indigo-700"
          }`}
        >
          {actionLabel} code
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          ) : null}
          <div>
            <label
              htmlFor="status-reason"
              className="block text-xs font-medium text-slate-700"
            >
              Reason (required)
            </label>
            <textarea
              id="status-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you are changing the code status…"
              rows={3}
              maxLength={500}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <p className="mt-0.5 text-xs text-slate-400">
              {reason.length}/500 characters (minimum 5)
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isSubmitting || reason.trim().length < 5}
              className={`rounded-md px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${
                isDeactivating
                  ? "bg-amber-600 hover:bg-amber-700"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              {isSubmitting ? `${actionLabel}ing…` : `Confirm ${actionLabel.toLowerCase()}`}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(null); setReason(""); }}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function AccessDenied() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-semibold text-slate-500">Not found</p>
      <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
        This page is not available
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
        Access code details are only available to authorised operators.
      </p>
    </section>
  );
}

export function AdminPromoCodeDetail() {
  const { id } = useParams<{ id: string }>();
  const { getToken, isLoaded } = useAuth();
  const [data, setData] = useState<AdminPromoCodeDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDenied, setIsDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    async function loadCode() {
      if (!isLoaded || !id) return;

      setIsLoading(true);
      setIsDenied(false);
      setError(null);

      try {
        const token = await getToken();
        setData(await getAdminPromoCodeDetail(token, id));
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          if (err.status === 404) {
            setError("Access code not found.");
          } else {
            setIsDenied(true);
          }
          return;
        }
        setError(
          err instanceof Error
            ? err.message
            : "Unable to load access code details.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    void loadCode();
  }, [id, getToken, isLoaded, refreshKey]);

  if (isDenied) {
    return <AccessDenied />;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin/promo-codes"
        className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to Access Codes
      </Link>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold text-indigo-700">Admin</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
          Access Code Detail
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
          View usage history and manage the status of this access code.
        </p>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-4">
          <div className="h-48 animate-pulse rounded-lg bg-white" />
          <div className="h-32 animate-pulse rounded-lg bg-white" />
        </div>
      ) : data ? (
        <>
          <CodeSummaryCard code={data.code} />
          <StatusControlCard
            code={data.code}
            onStatusChanged={() => setRefreshKey((k) => k + 1)}
          />
          <UsageHistoryCard uses={data.uses} />
        </>
      ) : null}
    </div>
  );
}
